import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaChartBar, FaSignOutAlt, FaClipboardList, FaBriefcase,
  FaBuilding, FaUsers, FaMoneyBillWave, FaFileExcel,
  FaUser, FaEnvelope, FaPhone, FaUserTie, FaCalendarAlt,
  FaHourglassHalf, FaInbox, FaEdit, FaCheckCircle, FaTimesCircle
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import logo from '../images/logo.png';
import {
  getAllApplications,
  getFilteredApplications,
  updateApplicationStatus,
  deleteApplication,
} from '../utils/api';
import './AdminDashboard.css';

const STATUS_OPTIONS = ['Pending', 'Under Review', 'Approved', 'Rejected'];

const statusColors = {
  'Pending':      { color: '#b45309', bg: '#fffbeb', border: '#fcd34d' },
  'Under Review': { color: '#1d4ed8', bg: '#eff6ff', border: '#93c5fd' },
  'Approved':     { color: '#065f46', bg: '#ecfdf5', border: '#6ee7b7' },
  'Rejected':     { color: '#991b1b', bg: '#fef2f2', border: '#fca5a5' },
};

const AdminDashboard = () => {
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [filter, setFilter] = useState({ type: '', status: '' });
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  // Status update modal state
  const [editingApp, setEditingApp] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateMsg, setUpdateMsg] = useState('');

  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');

  const loadApplications = async () => {
    setIsLoading(true);
    try {
      const apps = await getAllApplications(token);
      setApplications(apps);
      setFilteredApplications(apps);
    } catch (error) {
      console.error('Error loading applications:', error);
      if (error.message.includes('401') || error.message.includes('403')) {
        handleLogout();
      }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadApplications();
  }, []);

  useEffect(() => {
    const applyFilter = async () => {
      try {
        const filtered = await getFilteredApplications(token, filter.type, filter.status);
        setFilteredApplications(filtered);
      } catch (error) {
        console.error('Error filtering:', error);
      }
    };
    applyFilter();
  }, [filter.type, filter.status]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter(prev => ({ ...prev, [name]: value }));
  };

  const openStatusModal = (app) => {
    setEditingApp(app);
    setNewStatus(app.status || 'Pending');
    setStatusMessage(app.status_message || '');
    setUpdateMsg('');
  };

  const closeModal = () => {
    setEditingApp(null);
    setNewStatus('');
    setStatusMessage('');
    setUpdateMsg('');
  };

  const handleStatusUpdate = async () => {
    if (!editingApp || !newStatus) return;
    setIsUpdating(true);
    try {
      await updateApplicationStatus(token, editingApp.id, newStatus, statusMessage);
      setUpdateMsg('✅ Status updated successfully!');
      // Update local state
      const updater = (app) =>
        app.id === editingApp.id
          ? { ...app, status: newStatus, status_message: statusMessage }
          : app;
      setApplications(prev => prev.map(updater));
      setFilteredApplications(prev => prev.map(updater));
      setTimeout(closeModal, 1200);
    } catch (err) {
      setUpdateMsg('❌ Failed to update status: ' + err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;
    try {
      await deleteApplication(token, id);
      setApplications(prev => prev.filter(a => a.id !== id));
      setFilteredApplications(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  const exportToExcel = async () => {
    setIsExporting(true);
    await new Promise(r => setTimeout(r, 800));
    const ws = XLSX.utils.json_to_sheet(filteredApplications);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'LoanApplications');
    XLSX.writeFile(wb, `loan_applications_${new Date().toISOString().split('T')[0]}.xlsx`);
    setIsExporting(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  return (
    <div className="admin-dashboard">
      <div className="glass-container">
        {/* Header */}
        <div className="dashboard-header">
          <div className="admin-header-title">
            <img src={logo} alt="Logo" className="admin-dashboard-logo" />
            <h1>Admin Dashboard</h1>
          </div>
          <button onClick={handleLogout} className="logout-btn">
            <FaSignOutAlt style={{ marginRight: 8 }} /> Logout
          </button>
        </div>

        {/* Stats */}
        <div className="dashboard-stats">
          <div className="stat-card">
            <div className="stat-icon"><FaClipboardList /></div>
            <div className="stat-info">
              <h3>{isLoading ? '...' : applications.length}</h3>
              <p>Total Applications</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><FaBriefcase /></div>
            <div className="stat-info">
              <h3>{isLoading ? '...' : applications.filter(a => (a.customerType || a.customer_type) === 'Salaried').length}</h3>
              <p>Salaried</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><FaBuilding /></div>
            <div className="stat-info">
              <h3>{isLoading ? '...' : applications.filter(a => (a.customerType || a.customer_type) === 'Business Owner').length}</h3>
              <p>Business Owners</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><FaCheckCircle /></div>
            <div className="stat-info">
              <h3>{isLoading ? '...' : applications.filter(a => a.status === 'Approved').length}</h3>
              <p>Approved</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="filters">
          <div className="filter-group">
            <label><FaUsers style={{ marginRight: 8 }} /> Customer Type</label>
            <select name="type" value={filter.type} onChange={handleFilterChange}>
              <option value="">All Types</option>
              <option value="Salaried">Salaried</option>
              <option value="Business Owner">Business Owner</option>
            </select>
          </div>
          <div className="filter-group">
            <label><FaMoneyBillWave style={{ marginRight: 8 }} /> Loan Type</label>
            <select name="status" value={filter.status} onChange={handleFilterChange}>
              <option value="">All Loans</option>
              <option value="Home">Home Loan</option>
              <option value="Loan Against Property">Loan Against Property</option>
              <option value="Business">Business Loan</option>
              <option value="Personal">Personal Loan</option>
              <option value="Car">Car Loan</option>
            </select>
          </div>
          <button onClick={exportToExcel} className="export-btn" disabled={isExporting || filteredApplications.length === 0}>
            {isExporting ? <><FaHourglassHalf style={{ marginRight: 8 }} /> Exporting...</> : <><FaFileExcel style={{ marginRight: 8 }} /> Export to Excel</>}
          </button>
        </div>

        {/* Table */}
        <div className="applications-table">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th><FaUser style={{ marginRight: 6 }} /> Name</th>
                <th><FaEnvelope style={{ marginRight: 6 }} /> Email</th>
                <th><FaPhone style={{ marginRight: 6 }} /> Phone</th>
                <th><FaUserTie style={{ marginRight: 6 }} /> Type</th>
                <th><FaMoneyBillWave style={{ marginRight: 6 }} /> Loan</th>
                <th><FaMoneyBillWave style={{ marginRight: 6 }} /> Amount</th>
                <th>Status</th>
                <th><FaCalendarAlt style={{ marginRight: 6 }} /> Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="10" className="no-data">
                    <div className="no-data-message">
                      <div className="no-data-icon"><FaHourglassHalf className="spin" /></div>
                      <p>Loading applications...</p>
                    </div>
                  </td>
                </tr>
              ) : filteredApplications.length > 0 ? (
                filteredApplications.map((app, index) => {
                  const customerType = app.customerType || app.customer_type;
                  const loanType = app.loanType || app.loan_type;
                  const loanAmount = app.loanAmount || app.loan_amount;
                  const appDate = app.applicationDate || app.application_date || app.created_at;
                  const appStatus = app.status || 'Pending';
                  const sc = statusColors[appStatus] || statusColors['Pending'];

                  return (
                    <tr key={app.id || index}>
                      <td style={{ color: '#64748b', fontSize: '0.85rem' }}>{app.id}</td>
                      <td>{app.name}</td>
                      <td style={{ fontSize: '0.9rem' }}>{app.email}</td>
                      <td>{app.phone}</td>
                      <td>
                        <span className={`status-badge ${customerType === 'Salaried' ? 'salaried' : 'business'}`}>
                          {customerType}
                        </span>
                      </td>
                      <td><span className="loan-type-badge">{loanType}</span></td>
                      <td>₹{parseInt(loanAmount || 0).toLocaleString('en-IN')}</td>
                      <td>
                        <span
                          className="app-status-pill"
                          style={{ color: sc.color, background: sc.bg, borderColor: sc.border }}
                        >
                          {appStatus}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.9rem' }}>
                        {appDate ? new Date(appDate).toLocaleDateString('en-IN') : 'N/A'}
                      </td>
                      <td>
                        <div className="action-btns">
                          <button
                            className="action-btn edit-btn"
                            onClick={() => openStatusModal(app)}
                            title="Update Status"
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="action-btn delete-btn"
                            onClick={() => handleDelete(app.id)}
                            title="Delete"
                          >
                            <FaTimesCircle />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="10" className="no-data">
                    <div className="no-data-message">
                      <div className="no-data-icon"><FaInbox /></div>
                      <p>No applications found</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Status Update Modal */}
      {editingApp && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h2 style={{ color: 'var(--federal-blue, #002f6c)', marginBottom: '0.25rem' }}>Update Application Status</h2>
            <p style={{ color: '#64748b', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
              Application #{editingApp.id} — {editingApp.name}
            </p>

            <div className="modal-field">
              <label>New Status</label>
              <div className="status-options">
                {STATUS_OPTIONS.map(s => {
                  const sc = statusColors[s];
                  return (
                    <button
                      key={s}
                      className={`status-option-btn ${newStatus === s ? 'selected' : ''}`}
                      style={newStatus === s ? { background: sc.bg, borderColor: sc.color, color: sc.color } : {}}
                      onClick={() => setNewStatus(s)}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="modal-field">
              <label>Message to applicant (optional)</label>
              <textarea
                rows={3}
                value={statusMessage}
                onChange={e => setStatusMessage(e.target.value)}
                placeholder="E.g. We need additional documents..."
                className="modal-textarea"
              />
            </div>

            {updateMsg && (
              <p style={{ color: updateMsg.startsWith('✅') ? '#059669' : '#dc2626', marginBottom: '1rem', fontWeight: 600 }}>
                {updateMsg}
              </p>
            )}

            <div className="modal-actions">
              <button className="modal-cancel-btn" onClick={closeModal} disabled={isUpdating}>Cancel</button>
              <button className="modal-save-btn" onClick={handleStatusUpdate} disabled={isUpdating}>
                {isUpdating ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
